import { onBeforeUnmount, onMounted } from 'vue'

/** Поля, ради которых выезжает клавиатура. */
const FIELDS = 'input, textarea, [contenteditable="true"]'

/**
 * Клавиатура выезжает не мгновенно, и до конца анимации высота видимой
 * области ещё старая. Этот срок с запасом покрывает анимацию iOS.
 */
const KEYBOARD_DELAY = 350

/** Воздух под полем: вплотную к клавиатуре оно читается плохо. */
const GAP = 16

/** Ближайший предок, который действительно прокручивается. */
function scrollParent(element: HTMLElement): HTMLElement | null {
  let node = element.parentElement

  while (node) {
    const { overflowY } = getComputedStyle(node)
    if (/auto|scroll/.test(overflowY) && node.scrollHeight > node.clientHeight) return node
    node = node.parentElement
  }

  return null
}

/**
 * Насколько клавиатура закрыла экран. В Telegram на iOS страница под ней не
 * сжимается: `innerHeight` остаётся прежним, меняется только видимая часть.
 */
function keyboardHeight(): number {
  const viewport = window.visualViewport
  if (!viewport) return 0

  return Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
}

/**
 * Поднимает поле над клавиатурой. Сам браузер этого не делает: для него поле
 * на экране, ведь layout-вьюпорт не изменился. Клиент печатает вслепую, а на
 * экране настроек не видит даже, в какое поле попал.
 *
 * Считаем сами: насколько низ поля ушёл под клавиатуру, на столько и
 * прокручиваем — либо контейнер с прокруткой (формы в шитах), либо страницу.
 *
 * Отдельно добавляем снизу место высотой с клавиатуру. Без него последние
 * поля формы поднять невозможно: прокрутка упирается в конец страницы
 * раньше, чем поле выходит из-под клавиатуры.
 */
export function useKeyboardScroll(): void {
  let timer: ReturnType<typeof setTimeout> | undefined

  /**
   * Чем добираем место снизу. У контейнера с прокруткой хватает отступа,
   * а вот страница его не замечает: корневые элементы здесь фиксированной
   * высоты с `border-box`, и `padding` только съедает содержимое вместо
   * того, чтобы удлинить прокрутку. Поэтому странице подкладываем распорку.
   */
  let padded: { host: HTMLElement; previous: string } | null = null
  let spacer: HTMLElement | null = null

  function dropRoom(): void {
    if (padded) {
      padded.host.style.paddingBottom = padded.previous
      padded = null
    }

    spacer?.remove()
  }

  function addRoom(host: HTMLElement | null, amount: number): void {
    dropRoom()

    if (host) {
      padded = { host, previous: host.style.paddingBottom }
      host.style.paddingBottom = `${amount}px`
      return
    }

    /* Распорку держим в body: внутри `#app` её снёс бы следующий рендер Vue */
    spacer ??= document.createElement('div')
    spacer.setAttribute('aria-hidden', 'true')
    spacer.style.height = `${amount}px`
    document.body.appendChild(spacer)
  }

  function reveal(element: HTMLElement): void {
    const scroller = scrollParent(element)
    const keyboard = keyboardHeight()

    if (keyboard > 0) addRoom(scroller, keyboard)

    /* Читаем высоту: без этого прокрутка упрётся в старые границы */
    void (scroller ?? document.documentElement).scrollHeight

    const viewport = window.visualViewport
    const visibleBottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight
    const hidden = element.getBoundingClientRect().bottom + GAP - visibleBottom

    if (hidden <= 0) return

    if (scroller) scroller.scrollBy({ top: hidden, behavior: 'smooth' })
    else window.scrollBy({ top: hidden, behavior: 'smooth' })
  }

  function onFocus(event: FocusEvent): void {
    const target = event.target
    if (!(target instanceof HTMLElement) || !target.matches(FIELDS)) return

    clearTimeout(timer)
    timer = setTimeout(() => reveal(target), KEYBOARD_DELAY)
  }

  /* Клавиатура ушла — место под ней больше не нужно, иначе внизу дыра */
  function onBlur(): void {
    clearTimeout(timer)
    timer = setTimeout(() => {
      const active = document.activeElement
      if (active instanceof HTMLElement && active.matches(FIELDS)) return

      dropRoom()
    }, KEYBOARD_DELAY)
  }

  onMounted(() => {
    document.addEventListener('focusin', onFocus)
    document.addEventListener('focusout', onBlur)
  })

  onBeforeUnmount(() => {
    clearTimeout(timer)
    dropRoom()
    document.removeEventListener('focusin', onFocus)
    document.removeEventListener('focusout', onBlur)
  })
}
